import React, { useState, useEffect } from 'react';
import { parseApproximateComponents } from '@/utils/taskLogic';
import { TIMING_YEAR_OPTIONS } from '@/utils/dateFormat';

// Structured "week number" control, as a peer of an exact date and free text rather than raw
// text - a promoted, top-level shortcut to the week-number shape `ApproximateTimingSelect`
// already supports internally. Produces/consumes the same canonical string understood by
// `parseApproximateComponents`/`getApproximateLabel`/`calculateInterval` in `src/utils/taskLogic.js`:
//   "week 45 2026"
// No parsing logic changes needed here or there - this is purely a second UI entry point to an
// existing format.

// Default fallback when this mode is entered with no existing value yet - the year *options*
// list itself is the shared TIMING_YEAR_OPTIONS (imported above), not derived from this.
const currentYear = new Date().getFullYear();

const selectClass = 'rounded border px-2 py-1.5 bg-white dark:bg-gray-800 text-sm';

// ISO week number for "today" - used as the sensible default when this mode is entered fresh.
const currentIsoWeek = () => {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

export default function WeekNumberSelect({ value, onChange }) {
  const initial = parseApproximateComponents(value);

  const [week, setWeek] = useState(initial?.week || String(currentIsoWeek()));
  const [year, setYear] = useState(initial?.year || String(currentYear));

  const emit = (next) => {
    if (next.week && next.year) {
      onChange(`week ${next.week} ${next.year}`);
    }
  };

  // Re-sync local state if the incoming value changes from outside (e.g. switching between
  // diary entries being edited).
  useEffect(() => {
    const p = parseApproximateComponents(value);
    if (p?.mode === 'week') {
      setWeek(p.week);
      setYear(p.year);
    }
  }, [value]);

  // First time this mode is entered (no existing week value yet) - emit a sensible default
  // immediately rather than leaving the field blank.
  useEffect(() => {
    if (parseApproximateComponents(value)?.mode !== 'week') {
      emit({ week, year });
    }
    // Intentionally run once on mount only - subsequent syncs are handled by the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-wrap gap-2">
      <select
        value={week}
        onChange={(e) => {
          setWeek(e.target.value);
          emit({ week: e.target.value, year });
        }}
        className={selectClass}
      >
        <option value="">Week…</option>
        {Array.from({ length: 53 }, (_, i) => i + 1).map((w) => (
          <option key={w} value={w}>
            Week {w}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => {
          setYear(e.target.value);
          emit({ week, year: e.target.value });
        }}
        className={selectClass}
      >
        {TIMING_YEAR_OPTIONS.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
